import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, IconButton, Badge, Tabs, Tab, Button,
  Card, CardContent, Chip, Divider, Stack, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Close, DoneAll, Notifications, NotificationsActive, CheckCircle,
  TrendingUp, TrendingDown, PriorityHigh, Info, OpenInNew, ShoppingCart,
  Shield, Speed, Check, VolumeUp, Launch,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notifications as notifApi } from '../../services/api';
import {
  checkNotificationPermission, requestNotificationPermission,
} from '../../services/desktopNotificationService';
import NotificationDetailModal from './NotificationDetailModal';
import toast from 'react-hot-toast';

export default function NotificationDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permission, setPermission] = useState(checkNotificationPermission());
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notifApi.list({ limit: 50 });
      if (res.data?.success) {
        setItems(res.data.data || []);
        setUnreadCount(res.data.meta?.unreadCount || 0);
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchNotifications();
      setPermission(checkNotificationPermission());
    }
  }, [open]);

  const handleEnableDesktop = async () => {
    const res = await requestNotificationPermission();
    setPermission(res);
    if (res === 'granted') {
      toast.success('Desktop Notifications Enabled!');
    } else if (res === 'denied') {
      toast.error('Permission denied in browser settings.');
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notifApi.markAsRead(id);
      setItems(prev => prev.map(item => item.id === id ? { ...item, is_read: 1 } : item));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      toast.error('Failed to mark read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notifApi.markAllAsRead();
      setItems(prev => prev.map(item => ({ ...item, is_read: 1 })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch (err) {
      toast.error('Failed to mark all read');
    }
  };

  const handleCardClick = (item) => {
    setSelectedItem(item);
    setModalOpen(true);
    if (!item.is_read) {
      handleMarkAsRead(item.id);
    }
  };

  const handleActionClick = (e, item, actionType) => {
    e.stopPropagation();
    handleMarkAsRead(item.id);
    onClose();
    if (actionType === 'BUY' || actionType === 'VIEW_TRADE') {
      navigate(`/equity-orders?symbol=${item.symbol || ''}`);
    } else {
      navigate('/trading');
    }
  };

  const handleViewDetails = (e, item) => {
    e.stopPropagation();
    onClose();
    navigate(`/notifications?id=${item.id}`);
  };

  const filteredItems = items.filter(item => {
    if (tab === 'ALL') return true;
    if (tab === 'UNREAD') return !item.is_read;
    if (tab === 'CRITICAL') return item.priority === 'CRITICAL';
    if (tab === 'EQUITY') return item.instrument_type === 'EQUITY' || item.category?.includes('EQUITY');
    if (tab === 'OPTION') return item.instrument_type === 'OPTION' || item.category?.includes('OPTION');
    return true;
  });

  const getPriorityChip = (priority) => {
    switch (priority) {
      case 'CRITICAL':
        return <Chip label="CRITICAL" color="error" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem' }} />;
      case 'HIGH':
        return <Chip label="HIGH" color="warning" size="small" sx={{ fontWeight: 700, fontSize: '0.65rem' }} />;
      case 'MEDIUM':
        return <Chip label="MEDIUM" color="info" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem' }} />;
      default:
        return <Chip label="LOW" color="default" size="small" sx={{ fontSize: '0.65rem' }} />;
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 460 }, bgcolor: '#0f172a', color: '#f8fafc' }
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <NotificationsActive sx={{ color: '#38bdf8', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Notification Center</Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>Real-time Trading Alerts & AI Updates</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton onClick={handleMarkAllRead} sx={{ color: '#38bdf8' }}>
                  <DoneAll />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        {permission !== 'granted' && (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={handleEnableDesktop} sx={{ fontWeight: 700 }}>
                Enable
              </Button>
            }
            sx={{ borderRadius: 0, bgcolor: '#1e293b', color: '#38bdf8' }}
          >
            Desktop Notifications are disabled.
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            '& .MuiTab-root': { color: '#94a3b8', minWidth: 'auto', px: 2, fontWeight: 600 },
            '& .Mui-selected': { color: '#38bdf8' },
          }}
        >
          <Tab label={`All (${items.length})`} value="ALL" />
          <Tab label={`Unread (${unreadCount})`} value="UNREAD" />
          <Tab label="Critical" value="CRITICAL" />
          <Tab label="Equity" value="EQUITY" />
          <Tab label="Options" value="OPTION" />
        </Tabs>

        <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={32} sx={{ color: '#38bdf8' }} />
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: '#64748b' }}>
              <Notifications sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>No notifications found</Typography>
              <Typography variant="caption">All trading updates will appear here in real-time.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {filteredItems.map((item) => (
                <Card
                  key={item.id}
                  onClick={() => handleCardClick(item)}
                  sx={{
                    bgcolor: item.is_read ? '#1e293b' : '#0f2942',
                    border: item.is_read ? '1px solid #334155' : '1px solid #0284c7',
                    color: '#fff',
                    borderRadius: 2,
                    boxShadow: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { border: '1px solid #38bdf8', transform: 'translateY(-1px)' },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getPriorityChip(item.priority)}
                        {item.symbol && (
                          <Chip
                            label={item.symbol}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#38bdf8', fontWeight: 700, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                        {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 0.5 }}>
                      {item.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#cbd5e1', whiteSpace: 'pre-line', fontSize: '0.85rem', mb: 1.5 }}>
                      {item.message}
                    </Typography>

                    {/* Financial Metrics Summary */}
                    {(item.entry_price || item.current_price || item.pnl !== null) && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'rgba(0,0,0,0.2)', p: 1, borderRadius: 1, mb: 1.5, fontSize: '0.75rem' }}>
                        {item.entry_price && <span>Entry: <b>₹{item.entry_price}</b></span>}
                        {item.current_price && <span>Current: <b>₹{item.current_price}</b></span>}
                        {item.exit_price && <span>Exit: <b>₹{item.exit_price}</b></span>}
                        {item.stop_loss && <span>SL: <b style={{ color: '#ef4444' }}>₹{item.stop_loss}</b></span>}
                        {item.target && <span>Target: <b style={{ color: '#22c55e' }}>₹{item.target}</b></span>}
                        {item.pnl !== null && (
                          <span style={{ color: item.pnl >= 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
                            P&L: {item.pnl >= 0 ? '+' : ''}₹{item.pnl}
                          </span>
                        )}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Launch />}
                          onClick={(e) => handleViewDetails(e, item)}
                          sx={{ color: '#38bdf8', borderColor: '#0284c7', fontSize: '0.7rem', py: 0.2 }}
                        >
                          Details
                        </Button>
                        {item.category?.includes('BUY') && (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ShoppingCart />}
                            onClick={(e) => handleActionClick(e, item, 'BUY')}
                            sx={{ fontSize: '0.7rem', py: 0.2 }}
                          >
                            BUY
                          </Button>
                        )}
                      </Box>

                      {!item.is_read && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMarkAsRead(item.id); }} sx={{ color: '#94a3b8' }}>
                          <Check fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>

      <NotificationDetailModal
        open={modalOpen}
        notification={selectedItem}
        onClose={() => { setModalOpen(false); setSelectedItem(null); }}
        onMarkAsRead={handleMarkAsRead}
      />
    </>
  );
}
